export type AccessoryCategoryDetail = {
  id: string;
  index: string;
  eyebrow: string;
  headline: string;
  overview: string;
  buyerTypes: string[];
  planningChecks: string[];
  coordination: string;
  metaDescription: string;
};

export const accessoryCategoryDetails: AccessoryCategoryDetail[] = [
  {
    id: "accessibility-safety",
    index: "01",
    eyebrow: "Accessible restroom planning",
    headline: "Commercial grab bars, shower seats, and safety-focused accessories.",
    overview:
      "Coordinate accessibility products with the room layout, wall backing, mounting heights, clear floor space, and the complete project accessibility review. Partition Stalls helps organize the product list so supportive fixtures are considered alongside partitions and the rest of the restroom package.",
    buyerTypes: ["Architects and specifiers", "Healthcare and senior-living teams", "Schools, recreation, and public facilities"],
    planningChecks: [
      "Confirm product type, length, diameter, and configuration",
      "Identify wall construction and required backing before installation",
      "Coordinate mounting location with fixtures, partitions, and clearances",
      "Review finish direction with other exposed restroom accessories",
      "Have the project team verify applicable accessibility requirements",
    ],
    coordination:
      "Share restroom plans, wall details, and the intended grab bar or seat layout. Where drawings are incomplete, note the wall type and fixture locations so missing information can be identified before pricing.",
    metaDescription:
      "Plan commercial grab bars, shower seats, and accessible restroom accessories with layout, backing, finish, and mounting guidance from Partition Stalls.",
  },
  {
    id: "handwashing-hygiene",
    index: "02",
    eyebrow: "Lavatory and hygiene systems",
    headline: "Soap dispensers, hand dryers, faucets, and handwashing accessories.",
    overview:
      "Build a coordinated handwashing zone around traffic, power, refill access, counter conditions, finish, and maintenance preferences. The right package accounts for how people move through the room and how facility teams service each product after turnover.",
    buyerTypes: ["Office and hospitality projects", "Education and institutional facilities", "High-traffic public restrooms"],
    planningChecks: [
      "Choose manual or touch-free operation where applicable",
      "Confirm wall-mounted, counter-mounted, recessed, or integrated installation",
      "Identify available electrical power for dryers and electronic products",
      "Coordinate capacity and refill access with facility operations",
      "Review projections, mounting heights, and adjacent fixture clearances",
    ],
    coordination:
      "Provide the lavatory or counter type, expected user volume, power availability, finish direction, and any basis-of-design products. We can organize compatible product categories without assuming one universal configuration.",
    metaDescription:
      "Source commercial soap dispensers, hand dryers, faucets, and hygiene accessories with mounting, power, refill, and finish coordination guidance.",
  },
  {
    id: "paper-waste",
    index: "03",
    eyebrow: "Paper and waste management",
    headline: "Commercial toilet paper holders, towel dispensers, and waste units.",
    overview:
      "Paper and waste accessories should match traffic, service frequency, wall conditions, available space, and facility supply standards. Combination units can consolidate functions, while separate fixtures can provide more flexible placement and replacement planning.",
    buyerTypes: ["Facility managers and purchasing teams", "Contractors and Division 10 buyers", "Public, education, and workplace projects"],
    planningChecks: [
      "Confirm roll, towel, or consumable format used by the facility",
      "Select surface-mounted, semi-recessed, recessed, or freestanding installation",
      "Coordinate rough openings and wall depth for recessed products",
      "Review capacity against expected traffic and service frequency",
      "Confirm door swings, compartment clearances, and accessible placement",
    ],
    coordination:
      "Send the accessory schedule when one exists. For unscheduled work, list the desired consumable type, approximate traffic, wall construction, and finish so the package can be narrowed efficiently.",
    metaDescription:
      "Plan commercial toilet paper dispensers, paper towel dispensers, waste receptacles, and combination units for public restroom projects.",
  },
  {
    id: "compartment-accessories",
    index: "04",
    eyebrow: "Inside the toilet compartment",
    headline: "Partition hardware, hooks, shelves, sanitary units, and replacement parts.",
    overview:
      "Complete toilet compartments with the hardware and user accessories that support operation, privacy, maintenance, and replacement needs. New projects require coordinated attachment and finish details; renovations may also require careful identification of existing parts and dimensions.",
    buyerTypes: ["Partition installers and contractors", "Facility maintenance teams", "Renovation and replacement projects"],
    planningChecks: [
      "Identify partition material, thickness, and existing manufacturer when known",
      "Confirm door handing, swing, latch, hinge, and keeper conditions",
      "Measure existing hardware hole spacing before ordering replacements",
      "Coordinate hooks, shelves, sanitary units, and tissue fixtures within the compartment",
      "Provide clear photos and dimensions for unidentified replacement parts",
    ],
    coordination:
      "For replacement work, close-up photos, overall compartment photos, panel thickness, and center-to-center measurements are often more useful than a general product name. For new work, submit the partition schedule and desired hardware finish.",
    metaDescription:
      "Source commercial toilet partition hardware, hooks, shelves, sanitary units, and replacement parts with identification and measurement guidance.",
  },
  {
    id: "mirrors-storage",
    index: "05",
    eyebrow: "Reflection, storage, and daily use",
    headline: "Commercial restroom mirrors, shelves, hooks, and storage accessories.",
    overview:
      "Mirrors and storage accessories influence both daily usability and the visual order of the room. Coordinate size, edge or frame style, mounting, wall condition, corrosion exposure, and accessory alignment with the lavatory, tile, lighting, and partition layout.",
    buyerTypes: ["Architects and interior designers", "Hospitality and workplace projects", "Locker rooms and public facilities"],
    planningChecks: [
      "Confirm mirror width, height, frame or edge condition, and mounting method",
      "Coordinate reflective surface location with lavatories and accessible use",
      "Review shelf and hook projection against circulation and door swings",
      "Choose finishes that relate to faucets, dispensers, and partition hardware",
      "Identify humid or wet conditions that affect product selection",
    ],
    coordination:
      "Provide the wall elevation or lavatory dimensions when possible. If the design is open, share the desired mirror format, approximate size, mounting surface, and finish direction for a focused options review.",
    metaDescription:
      "Plan commercial restroom mirrors, hooks, shelves, and storage accessories with size, mounting, finish, and layout coordination support.",
  },
  {
    id: "family-specialty",
    index: "06",
    eyebrow: "Family, healthcare, and specialty rooms",
    headline: "Baby changing stations, healthcare fixtures, and specialty restroom products.",
    overview:
      "Specialty restroom products require close attention to user needs, wall support, available clearances, cleaning protocols, and the surrounding accessory layout. Coordinate these fixtures early so their folded and open positions work with doors, partitions, circulation, and other equipment.",
    buyerTypes: ["Family and public restrooms", "Healthcare and assisted-use environments", "Transportation, recreation, and institutional facilities"],
    planningChecks: [
      "Confirm intended users, capacity, product orientation, and operating clearance",
      "Identify wall construction, backing, and installation responsibility",
      "Coordinate open and closed positions with circulation and adjacent fixtures",
      "Review cleaning, infection-control, or facility maintenance requirements",
      "Confirm power, drainage, or other utilities for specialty equipment where applicable",
    ],
    coordination:
      "Share the room plan, wall type, fixture schedule, and intended product function. Specialty products should be reviewed as part of the complete room rather than added after all available wall space has been assigned.",
    metaDescription:
      "Source baby changing stations, healthcare fixtures, and specialty commercial restroom accessories with layout, backing, and clearance guidance.",
  },
];

export type MountingStyle = {
  number: string;
  name: string;
  slug: string;
  shortLabel: string;
  image: { src: string; smallSrc: string; width: number; height: number; alt: string };
  selectorImage: { src: string; smallSrc: string; width: number; height: number; alt: string };
  summary: string;
  overview: string;
  bestFor: string;
  mainAdvantage: string;
  confirmFirst: string;
  compatibleMaterials: string[];
  considerations: string[];
};

export const mountingStyles: MountingStyle[] = [
  {
    number: "01",
    name: "Overhead-Braced",
    slug: "overhead-braced",
    shortLabel: "Familiar + economical",
    image: {
      src: "/manus-storage/mounting-overhead-braced-product-1280_4a907648.webp",
      smallSrc: "/manus-storage/mounting-overhead-braced-product-720_389fe821.webp",
      width: 1280,
      height: 960,
      alt: "Illustrative charcoal overhead-braced commercial bathroom partition system with a continuous headrail above three compartment doors",
    },
    selectorImage: {
      src: "/manus-storage/mounting-overhead-braced-selector-1280_b87ecb46.webp",
      smallSrc: "/manus-storage/mounting-overhead-braced-selector-720_c13698b8.webp",
      width: 1280,
      height: 960,
      alt: "Illustrative overhead-braced commercial bathroom partition configuration on a clean white background, with a continuous headrail above floor-supported pilasters",
    },
    summary:
      "Floor-supported pilasters connect with an overhead rail for a familiar and widely adaptable commercial system.",
    overview:
      "Overhead-Braced partitions use floor-anchored pilasters connected by a continuous headrail. This is a common starting point because it works with many layouts and does not rely on structural support above the finished ceiling.",
    bestFor:
      "Offices, retail, restaurants, schools, civic facilities, and other projects seeking a familiar commercial configuration.",
    mainAdvantage:
      "The overhead rail adds lateral stability while keeping the system adaptable to many common floor and ceiling conditions.",
    confirmFirst:
      "Verify the finished floor, wall construction, room dimensions, and whether a visible headrail fits the design direction.",
    compatibleMaterials: ["Powder Coated Steel", "Plastic Laminate", "Solid Plastic (HDPE)", "Stainless Steel", "Phenolic"],
    considerations: ["Finished floor and slab condition", "Overall compartment layout", "Ceiling conflicts above the rail", "Required wall backing and anchorage"],
  },
  {
    number: "02",
    name: "Floor-Mounted",
    slug: "floor-mounted",
    shortLabel: "Clean + open topline",
    image: {
      src: "/manus-storage/mounting-floor-mounted-product-1280_b6a5b2e7.webp",
      smallSrc: "/manus-storage/mounting-floor-mounted-product-720_da9ff6d5.webp",
      width: 1280,
      height: 960,
      alt: "Illustrative charcoal floor-mounted commercial bathroom partition system with floor-anchored pilasters and an open topline",
    },
    selectorImage: {
      src: "/manus-storage/mounting-floor-mounted-selector-1280_39ef8f00.webp",
      smallSrc: "/manus-storage/mounting-floor-mounted-selector-720_ededaa84.webp",
      width: 1280,
      height: 960,
      alt: "Illustrative floor-mounted commercial bathroom partition configuration on a clean white background, supported by visible floor-anchored pilasters with no headrail",
    },
    summary:
      "Pilasters anchor at the floor without an overhead rail, creating a clean and open line above the compartments.",
    overview:
      "Floor-Mounted partitions rely on secure floor anchorage rather than a headrail or ceiling suspension. The result is a cleaner upper profile, but the floor structure and manufacturer anchorage requirements become especially important.",
    bestFor:
      "Professional offices, hospitality, and architectural interiors that favor an open ceiling zone above the compartments.",
    mainAdvantage:
      "The system leaves the area above the partitions visually open without relying on ceiling suspension.",
    confirmFirst:
      "Confirm that the floor can accept the required anchors, including slab depth, reinforcement, waterproofing, and manufacturer requirements.",
    compatibleMaterials: ["Powder Coated Steel", "Plastic Laminate", "Stainless Steel", "Phenolic"],
    considerations: ["Structural floor suitability", "Pilaster height and support requirements", "Floor finish and waterproofing", "Manufacturer-specific anchorage"],
  },
  {
    number: "03",
    name: "Ceiling-Hung",
    slug: "ceiling-hung",
    shortLabel: "Open floor + easy cleaning",
    image: {
      src: "/manus-storage/mounting-ceiling-hung-product-1280_61f33cc7.webp",
      smallSrc: "/manus-storage/mounting-ceiling-hung-product-720_3176244f.webp",
      width: 1280,
      height: 960,
      alt: "Illustrative charcoal ceiling-hung commercial bathroom partition system with pilasters suspended from above and an open floor below",
    },
    selectorImage: {
      src: "/manus-storage/mounting-ceiling-hung-selector-1280_9f51bdb6.webp",
      smallSrc: "/manus-storage/mounting-ceiling-hung-selector-720_9ad8c53f.webp",
      width: 1280,
      height: 960,
      alt: "Illustrative ceiling-hung commercial bathroom partition configuration on a clean white background, suspended from above with the floor open below",
    },
    summary:
      "Pilasters suspend from overhead structure, leaving the finished floor below visually open and easier to access.",
    overview:
      "Ceiling-Hung partitions transfer pilaster support to framing above the finished ceiling. Keeping the floor below the pilasters open can simplify floor care, but the overhead structure and installation sequence must be coordinated early.",
    bestFor:
      "Airports, transit, healthcare, institutional, and premium commercial projects with suitable overhead structure.",
    mainAdvantage:
      "Keeping support points off the finished floor improves access for routine cleaning and creates a visually open base.",
    confirmFirst:
      "Confirm overhead structure, attachment points, ceiling coordination, and whether framing or blocking is required.",
    compatibleMaterials: ["Powder Coated Steel", "Plastic Laminate", "Stainless Steel", "Phenolic"],
    considerations: ["Structural support above", "Finished ceiling height", "Plenum and utility conflicts", "Installation sequence with ceiling trades"],
  },
  {
    number: "04",
    name: "Floor-to-Ceiling",
    slug: "floor-to-ceiling",
    shortLabel: "Rigid + substantial",
    image: {
      src: "/manus-storage/mounting-floor-to-ceiling-product-1280_73f3028e.webp",
      smallSrc: "/manus-storage/mounting-floor-to-ceiling-product-720_caf2ad9f.webp",
      width: 1280,
      height: 960,
      alt: "Illustrative charcoal floor-to-ceiling commercial bathroom partition system with full-height pilasters supported above and below",
    },
    selectorImage: {
      src: "/manus-storage/mounting-floor-to-ceiling-selector-1280_8b6c59a2.webp",
      smallSrc: "/manus-storage/mounting-floor-to-ceiling-selector-720_93c101f6.webp",
      width: 1280,
      height: 960,
      alt: "Illustrative floor-to-ceiling commercial bathroom partition configuration on a clean white background, with pilasters connected at both floor and overhead structure",
    },
    summary:
      "Pilasters connect at both floor and overhead structure for a rigid, substantial, and premium-feeling installation.",
    overview:
      "Floor-to-Ceiling partitions anchor pilasters at both the floor and the structure above. This vertically continuous support can create a substantial architectural presence when the room is designed to accept both connection points.",
    bestFor:
      "High-traffic facilities, taller privacy configurations, and architectural projects that prioritize rigidity.",
    mainAdvantage:
      "Support at both floor and overhead structure creates a highly stable system with a substantial architectural presence.",
    confirmFirst:
      "Confirm floor anchorage, overhead support, ceiling height, and service conflicts before the system is priced.",
    compatibleMaterials: ["Powder Coated Steel", "Plastic Laminate", "Solid Plastic (HDPE)", "Stainless Steel", "Phenolic"],
    considerations: ["Floor and ceiling structure", "Exact finished ceiling height", "Variation across the room", "Top and bottom anchorage details"],
  },
];

export type PrivacyOption = {
  number: string;
  slug: string;
  name: string;
  eyebrow: string;
  image: { src: string; width: number; height: number; alt: string };
  heightLabel: string;
  clearanceLabel: string;
  panelTop: number;
  panelBottom: number;
  gapLabel: string;
  diagramHeightLabel: string;
  diagramClearanceLabel: string;
  gapDetailLabel: string;
  gapDetail: string;
  overview: string;
  bestFor: string;
  floorDifference: string;
  confirmFirst: string;
};

export const privacyOptions: PrivacyOption[] = [
  {
    number: "01",
    slug: "standard-privacy",
    name: "Standard Privacy",
    eyebrow: "58 in. coverage / 12 in. floor clearance",
    image: {
      src: "/manus-storage/privacy-standard-58-inch_a0a60aac.webp",
      width: 576,
      height: 384,
      alt: "Dark powder-coated headrail-braced restroom partition configuration with Standard Privacy panel and door coverage",
    },
    heightLabel: "58 in. panels and doors",
    clearanceLabel: "12 in. above finished floor",
    panelTop: 55,
    panelBottom: 142,
    gapLabel: "Largest opening below the panel",
    diagramHeightLabel: "58 in. PANEL + DOOR",
    diagramClearanceLabel: "12 in. AFF",
    gapDetailLabel: "Standard door and panel joints",
    gapDetail: "Use the selected manufacturer’s standard door and panel connection details unless the project calls for added sightline reduction.",
    overview: "A familiar powder-coated, headrail-braced partition direction with a conventional 58-inch panel-and-door height and a 12-inch opening above the finished floor.",
    bestFor: "Projects comparing a familiar commercial baseline with taller privacy-focused options.",
    floorDifference: "The 12-inch opening leaves the most visible space below the panels in this four-option comparison.",
    confirmFirst: "Confirm actual available height, floor clearance, door detail, accessible-compartment conditions, and project requirements with the selected manufacturer system.",
  },
  {
    number: "02",
    slug: "privacy-plus",
    name: "Privacy Plus",
    eyebrow: "64 in. coverage / 6 or 9 in. floor clearance",
    image: {
      src: "/manus-storage/privacy-plus-64-inch_63f72a0d.webp",
      width: 576,
      height: 384,
      alt: "Dark powder-coated headrail-braced restroom partition configuration with Privacy Plus panel and door coverage",
    },
    heightLabel: "64 in. panels and doors",
    clearanceLabel: "6 or 9 in. above finished floor",
    panelTop: 53,
    panelBottom: 149,
    gapLabel: "Reduced opening below the panel",
    diagramHeightLabel: "64 in. PANEL + DOOR",
    diagramClearanceLabel: "6 or 9 in. AFF",
    gapDetailLabel: "Continuous-channel direction",
    gapDetail: "Continuous channels can be coordinated at panel connections to reduce common sightlines where the selected system provides them.",
    overview: "A taller privacy direction that moves the panel-and-door coverage to 64 inches and lowers the floor opening to either 6 or 9 inches above the finished floor.",
    bestFor: "Commercial restrooms seeking a more enclosed appearance while retaining a headrail-braced powder-coated configuration.",
    floorDifference: "The 6- or 9-inch opening noticeably reduces the amount of visible space below the panels compared with Standard Privacy.",
    confirmFirst: "Confirm floor clearance, continuous-channel availability, door detail, mounting compatibility, cleaning access, and accessible-compartment conditions.",
  },
  {
    number: "03",
    slug: "max-privacy",
    name: "Max Privacy",
    eyebrow: "72 in. coverage / 6 or 9 in. floor clearance",
    image: {
      src: "/manus-storage/privacy-max-72-inch_1522b324.webp",
      width: 576,
      height: 384,
      alt: "Dark powder-coated headrail-braced restroom partition configuration with Max Privacy panel and door coverage",
    },
    heightLabel: "72 in. panels and doors",
    clearanceLabel: "6 or 9 in. above finished floor",
    panelTop: 41,
    panelBottom: 149,
    gapLabel: "Tall coverage with a reduced opening",
    diagramHeightLabel: "72 in. PANEL + DOOR",
    diagramClearanceLabel: "6 or 9 in. AFF",
    gapDetailLabel: "Continuous-channel direction",
    gapDetail: "This comparison direction combines taller coverage with continuous-channel planning where available to reduce common door and side sightlines.",
    overview: "A 72-inch panel-and-door direction that adds vertical coverage above the compartment while retaining either a 6- or 9-inch opening above the finished floor.",
    bestFor: "Commercial restroom projects seeking taller coverage with a consistent, coordinated privacy treatment.",
    floorDifference: "The lower opening remains in the 6- or 9-inch range, while the taller panel-and-door height provides the larger visual privacy change.",
    confirmFirst: "Confirm the available 72-inch configuration, floor clearance, continuous-channel detail, mounting, maintenance access, and accessible-compartment conditions before quoting.",
  },
  {
    number: "04",
    slug: "ultimate-privacy",
    name: "Ultimate Privacy",
    eyebrow: "84 or 92 in. coverage / 4, 6, or 9 in. floor clearance",
    image: {
      src: "/manus-storage/privacy-ultimate-84-92-inch_c678ce23.webp",
      width: 576,
      height: 384,
      alt: "Dark powder-coated headrail-braced restroom partition configuration with Ultimate Privacy panel and door coverage",
    },
    heightLabel: "84 or 92 in. panels and doors",
    clearanceLabel: "4, 6, or 9 in. above finished floor",
    panelTop: 16,
    panelBottom: 155,
    gapLabel: "Smallest listed opening below the panel",
    diagramHeightLabel: "84 or 92 in. PANEL + DOOR",
    diagramClearanceLabel: "4, 6, or 9 in. AFF",
    gapDetailLabel: "Full-height detail coordination",
    gapDetail: "Coordinate continuous channels, a continuous stop, hinge-side fillers, door hardware, ventilation, and mounting conditions with the selected system.",
    overview: "The highest-coverage comparison direction, with 84- or 92-inch panels and doors and a 4-, 6-, or 9-inch opening above the finished floor depending on the selected configuration.",
    bestFor: "Projects where a more enclosed partition appearance is a design priority and the full coordination requirements can be addressed early.",
    floorDifference: "The available 4-inch option creates the smallest opening in this comparison; final clearance is selected with the actual system and project requirements.",
    confirmFirst: "Confirm configuration availability, floor and overhead conditions, ventilation coordination, door hardware, maintenance access, and applicable accessibility requirements before pricing.",
  },
];

export const measuringSections = [
  {
    number: "01",
    title: "Start with the room",
    intro: "Record the finished inside dimensions that define the partition layout.",
    items: ["Overall room width and depth", "Wall returns, chases, columns, and offsets", "Finished floor elevation and slopes", "Finished ceiling height where mounting depends on it"],
  },
  {
    number: "02",
    title: "Locate every fixture",
    intro: "Measure from consistent finished surfaces and identify fixture centerlines.",
    items: ["Water closet centerlines", "Urinal centerlines and screen locations", "Lavatories, counters, and accessory zones", "Floor drains, cleanouts, and obstacles affecting pilasters"],
  },
  {
    number: "03",
    title: "Document openings and movement",
    intro: "Show how people and doors move through the room and compartments.",
    items: ["Entry door location and swing", "Partition door handing and swing preference", "Required circulation paths", "Wheelchair-accessible and ambulatory toilet compartment locations and approved project design dimensions"],
  },
  {
    number: "04",
    title: "Describe the surfaces",
    intro: "The substrate and finished condition affect attachment and coordination.",
    items: ["Wall construction and finish", "Known backing locations", "Floor and ceiling construction", "Base, tile, or trim conditions at attachment points"],
  },
  {
    number: "05",
    title: "Send plans and photos",
    intro: "Visual context helps identify conditions that dimensions alone may miss.",
    items: ["Dimensioned plan or clear hand sketch", "Wide photos of every wall", "Close-ups of existing hardware for replacement work", "Project location, material preference, quantity, and schedule context"],
  },
];

export const finishGuidance = [
  { material: "Powder Coated Steel", direction: "Baked-on color finishes support straightforward coordination across many commercial interiors.", confirm: "Approved manufacturer palette, sheen, hardware finish, and touch-up expectations." },
  { material: "Plastic Laminate", direction: "Decorative colors, patterns, and wood-inspired surfaces support design-led dry interiors.", confirm: "Manufacturer pattern, directional layout, edge treatment, and controlled-moisture suitability." },
  { material: "Solid Plastic (HDPE)", direction: "Integral color and available textures create a durable, practical expression for demanding facilities.", confirm: "Manufacturer color and texture, panel thickness, hardware finish, and cleaning expectations." },
  { material: "Stainless Steel", direction: "Metal surfaces create a precise architectural character and coordinate naturally with stainless accessories.", confirm: "Surface finish, grain direction, protective handling, and accessory finish coordination." },
  { material: "Phenolic", direction: "Compact panels can support contemporary solid colors, patterns, and refined edge expressions.", confirm: "Manufacturer finish, core and edge appearance, texture, panel thickness, and chemical exposure." },
];

export const faqs = [
  { question: "What information should I send for a commercial bathroom partition quote?", answer: "Send the project location, material preference, approximate compartment quantity, mounting style if known, desired finish direction, plans or dimensions, and delivery requirements. If the scope is incomplete, identify what is known and Partition Stalls can flag the missing inputs needed to continue." },
  { question: "Which commercial bathroom partition material is best for wet or humid restrooms?", answer: "Solid Plastic (HDPE) and Phenolic are commonly considered where moisture resistance is a priority. The final selection should account for direct water exposure, cleaning practices, traffic, design goals, budget, and the project specification." },
  { question: "When should Plastic Laminate partitions be considered?", answer: "Plastic Laminate is well suited to dry, climate-controlled interiors where decorative color, pattern, or wood-inspired finishes are important. It should not be treated as a universal choice for wet or persistently humid environments." },
  { question: "What mounting styles are available for toilet partitions?", answer: "Common systems include overhead-braced, floor-anchored, ceiling-hung, and floor-to-ceiling. Floor structure, overhead support, finished ceiling height, utilities, appearance, installation sequence, and manufacturer requirements all affect the choice." },
  { question: "Can Partition Stalls help with commercial restroom accessories as well as partitions?", answer: "Yes. The catalog covers grab bars, shower seats, mirrors, hand dryers, soap and paper dispensers, waste units, compartment accessories, hooks, shelves, changing stations, healthcare products, and replacement hardware." },
  { question: "Do you supply toilet partition replacement hardware and parts?", answer: "For bathroom stall hinges, latches, keepers, brackets, pilaster shoes, or other replacement hardware, send clear photos, partition material, panel thickness, visible manufacturer markings, hole spacing, dimensions, and door handing. Replacement support depends on identifying the existing system; compatibility and availability must be confirmed for the actual part, not assumed from appearance." },
  { question: "How should I plan ADA bathroom stalls or ambulatory toilet compartments?", answer: "Share the approved project layout, accessible and ambulatory compartment locations, design dimensions, fixture centerlines, approach spaces, door swings, and accessory requirements. The selected partition system must be coordinated with the project design team and applicable accessibility requirements. A material choice, planning budget, or preliminary measurement does not certify ADA compliance." },
  { question: "Can I request partitions and accessories in the same quote?", answer: "Yes. Combining the partition scope and accessory schedule helps coordinate finish, quantities, room coverage, delivery, and Division 10 purchasing through one project conversation." },
  { question: "Does Partition Stalls supply projects nationwide?", answer: "Partition Stalls supports commercial restroom projects across the United States. Delivery details, site access, quantities, and project schedule should be included with the quote request so logistics can be reviewed for the specific job." },
  { question: "Do I need finished architectural drawings before requesting pricing?", answer: "No. Drawings are helpful, but an early request can begin with dimensions, fixture locations, photos, quantities, material preference, and a description of the project. Unresolved items can be identified before a final quote is prepared." },
  { question: "How do I choose colors and finishes?", answer: "Begin with the selected material family, then review the approved manufacturer palette and samples for that product. Coordinate partition finish with tile, paint, counters, hardware, grab bars, dispensers, mirrors, and the expected cleaning environment." },
  { question: "Does the website show every available manufacturer color or configuration?", answer: "No. Availability changes by material, manufacturer, series, and project conditions. The website provides selection guidance; current samples, technical data, and approved options should be reviewed during the quote and submittal process." },
  { question: "How quickly will I receive a quote?", answer: "Response time depends on scope completeness, product selection, quantities, drawings, and whether clarifications are required. Providing the project details listed in the quote form helps reduce avoidable follow-up before pricing." },
];

export const resourceLinks = [
  { number: "01", href: "/resources/buyer-guides", title: "Buyer Guides", copy: "Plan material selection, an early budget, a useful quote request, and a supplier comparison around the real project." },
  { number: "02", href: "/resources/mounting-styles", title: "Mounting Styles", copy: "Compare four common commercial toilet partition support systems and the conditions to confirm." },
  { number: "03", href: "/resources/privacy-options", title: "Privacy Options", copy: "Compare common privacy-height planning ranges, door and side-gap details, and the open floor below partitions." },
  { number: "04", href: "/resources/how-to-measure", title: "How to Measure", copy: "Prepare wall-to-wall dimensions, layout details, and project information for a focused quote." },
  { number: "05", href: "/resources/colors-finishes", title: "Colors & Finishes", copy: "Coordinate partition materials, colors, patterns, textures, and accessory finishes." },
  { number: "06", href: "/resources/faqs", title: "Project FAQs", copy: "Get straightforward answers on materials, measuring, accessories, delivery, and quote preparation." },
];
